import React, { useCallback, useMemo, useState } from "react";
import { IRepertoireField } from "../../../repertoire/types/IRepertoireField";
import { AddToPackagesHandler } from "../../../repertoire/types/AddToPackagesHandler";
import { useAddToPackageContext } from "./AddToPackageContext";
import { IYesNoPromptViewModalProps } from "../../../repertoire/components/modalViews/yesNoPromptView/YesNoPromptView";
import { showYesNoPrompt, hideModal } from "../../../redux/reducers/ModalReducer";
import { ADD_ALL_TO_PACKAGE_HEADER_STATE_KEY, ADD_ALL_TO_PACKAGE_PROMPT_STATE_KEY } from "../../../repertoire/Consts";
import { CURRENT_USER_EMAIL_KEY } from "../../../redux/services/AccountSessionStorage";

interface AddAllToPackageButtonProps {
    entityIds: number[];
    addToPackages?: AddToPackagesHandler;
    searchResultsTableData: IRepertoireField[];
    entityType: string;
    showYesNoPrompt: typeof showYesNoPrompt;
    hideModal: typeof hideModal;
    disabled?: boolean;
    componentInstance?: string;
    deferredSelection?: boolean;
}

const AddAllToPackageButton: React.FC<AddAllToPackageButtonProps> = ({
    entityIds,
    addToPackages,
    searchResultsTableData,
    entityType,
    showYesNoPrompt,
    hideModal,
    disabled = false,
    componentInstance,
    deferredSelection = false,
}) => {
    const [messageText, setMessageText] = useState("");
    const [messageType, setMessageType] = useState<"success" | "error" | undefined>(undefined);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDone, setIsDone] = useState(false);

    const { addedIds, selectedIds, addIds, selectIds, clearSelectedIds } = useAddToPackageContext();

    const selectableEntityIds = useMemo(() => {
        const addedIdSet = new Set(addedIds);
        return Array.from(new Set(entityIds.filter(Number.isFinite)))
            .filter(id => !addedIdSet.has(id));
    }, [addedIds, entityIds]);

    const messageClass =
        messageType === "error"
            ? "addtopackageerrorMessage"
            : "addtopackagesuccessMessage";

    const addAllToPackagePrompt: string =
        searchResultsTableData.find(f =>
            f.name === ADD_ALL_TO_PACKAGE_PROMPT_STATE_KEY &&
            (!componentInstance || f.componentInstance === componentInstance)
        )?.data;

    const addAllToPackageHeader: string =
        searchResultsTableData.find(f =>
            f.name === ADD_ALL_TO_PACKAGE_HEADER_STATE_KEY &&
            (!componentInstance || f.componentInstance === componentInstance)
        )?.data;

    const runAddAll = useCallback(async () => {
        if (disabled || !addToPackages || entityIds.length === 0) {
            return;
        }

        const username = sessionStorage.getItem(CURRENT_USER_EMAIL_KEY);
        if (!username) {
            return;
        }

        setIsProcessing(true);

        const response = await addToPackages(username, entityType, entityIds);

        const messageKey =
            response?.messages?.[0]?.messageKey;

        const configuredMessage =
            searchResultsTableData.find(
                field =>
                    field.name?.toString() === messageKey
            )?.data;

        const displayMessage =
            configuredMessage ||
            (response?.success
                ? "Added to package"
                : "Could not add to package");

        if (response?.success) {
            const packageItems = response.MyPackages?.packageItems ?? [];

            addIds(packageItems);
            setMessageType("success");
            setIsDone(true);
        } else {
            setMessageType("error");
            setIsDone(false);
        }

        setMessageText(displayMessage);

        setTimeout(() => {
            setMessageText('');
        }, 5000);

        setIsProcessing(false);
    }, [addToPackages, entityIds, entityType, searchResultsTableData, addIds, disabled]);

    const handleClick = useCallback(() => {
        if (deferredSelection) {
            if (selectableEntityIds.length === 0) {
                return;
            }

            const selectedIdSet = new Set(selectedIds);
            const isCurrentPageSelected = selectableEntityIds.every(id => selectedIdSet.has(id));

            if (isCurrentPageSelected) {
                clearSelectedIds(selectableEntityIds);
            } else {
                selectIds(selectableEntityIds);
            }
            return;
        }

        const propsModal: IYesNoPromptViewModalProps = {
            yesCallback: async () => {
                await runAddAll();
                hideModal();
            },
            noCallback: () => {
                hideModal();
            }
        };
        showYesNoPrompt(addAllToPackageHeader, propsModal, undefined, addAllToPackagePrompt);
    }, [runAddAll, showYesNoPrompt, hideModal, addAllToPackagePrompt, deferredSelection, selectableEntityIds, selectedIds, clearSelectedIds, selectIds]);

    return (
        <div className="addAllToPackageButtonWrapper">
            <button
                type="button"
                className={["add-all-to-package-button", isProcessing && "is-loading", isDone && "is-success", messageType === "error" && "is-error",].filter(Boolean).join(" ")}
                onClick={handleClick}
                disabled={disabled || isProcessing || (deferredSelection && selectableEntityIds.length === 0)}
            >
                { isProcessing ?
                    searchResultsTableData.find(f => f.name.toString() === "processingSelectAllForPackage")?.data :
                    searchResultsTableData.find(f => f.name.toString() === "selectAllForPackage")?.data }
            </button>

            {messageText && (
                <div className={`messageBox ${messageClass}`}>{messageText}</div>
            )}
        </div>
    );
};

export default AddAllToPackageButton;
